/**
 * The agent's workflow list (`GET /workflows`): each workflow's state, whether it
 * is ready to start on this machine, the sections its view is composed of, and
 * its card text in both languages.
 *
 * An unclear answer is **not** flattened to "none". The Workflows page has to
 * tell three things apart: the agent lists nothing (every card hidden), the
 * agent is too old to have the route (404 — say to update the desktop app), and
 * the agent is not reachable (say so, as the composer does). Pure, so the tests
 * transpile this file alone.
 */

import type { WorkflowListing, WorkflowRunMode, WorkflowSectionId } from "../../../shared/workflow-listing";
import type { Locale } from "../i18n/strings";
import type { AgentClient } from "./agentClient";

/** The sections this build can draw; a newer agent's unknown one is left out, not fatal. */
const SECTIONS: ReadonlySet<string> = new Set<WorkflowSectionId>(["setup", "progress", "results"]);
const MODES: ReadonlySet<string> = new Set<WorkflowRunMode>(["hitl", "auto", "plan"]);

/** The agent facts: a forced mode only when it is one we know; Save Skill offered unless the agent says not. */
function parseAgentFacts(value: unknown): WorkflowListing["agent"] {
  const facts = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const mode = facts["mode"];
  return {
    mode: typeof mode === "string" && MODES.has(mode) ? (mode as WorkflowRunMode) : null,
    saveSkill: facts["saveSkill"] !== false,
  };
}

export type WorkflowListResult =
  | { kind: "ok"; workflows: WorkflowListing[] }
  /** 404: an agent from before the route. */
  | { kind: "unsupported" }
  /** Not up, not JSON, or any other status. */
  | { kind: "unavailable" };

const STATES = new Set(["open", "coming-soon"]);
const isText = (value: unknown): value is WorkflowListing["name"] =>
  typeof value === "object" && value !== null && typeof (value as { en?: unknown }).en === "string" && typeof (value as Record<string, unknown>)["zh-TW"] === "string";

/** The listings a body carries, or null when it is not a list at all. Entries missing a field are dropped, not guessed. */
export function parseWorkflowList(body: unknown): WorkflowListing[] | null {
  const list = (body as { workflows?: unknown } | null)?.workflows;
  if (!Array.isArray(list)) return null;
  const out: WorkflowListing[] = [];
  for (const item of list as Array<Record<string, unknown>>) {
    if (!item || typeof item !== "object") continue;
    if (typeof item["id"] !== "string" || typeof item["code"] !== "string" || !item["code"]) continue;
    if (!STATES.has(item["state"] as string) || typeof item["category"] !== "string") continue;
    if (!isText(item["name"]) || !isText(item["description"])) continue;
    out.push({
      id: item["id"],
      code: item["code"],
      state: item["state"] as WorkflowListing["state"],
      category: item["category"] as WorkflowListing["category"],
      hosts: Array.isArray(item["hosts"]) ? (item["hosts"].filter((h): h is WorkflowListing["hosts"][number] => typeof h === "string") as WorkflowListing["hosts"]) : [],
      name: item["name"],
      description: item["description"],
      ...(isText(item["about"]) ? { about: item["about"] } : {}),
      sections: Array.isArray(item["sections"])
        ? (item["sections"].filter((s): s is WorkflowSectionId => typeof s === "string" && SECTIONS.has(s)))
        : [],
      agent: parseAgentFacts(item["agent"]),
      ready: item["ready"] === true,
      ...(typeof item["reason"] === "string" ? { reason: item["reason"] as WorkflowListing["reason"] } : {}),
    });
  }
  return out;
}

export async function fetchWorkflowList(client: Pick<AgentClient, "fetch">): Promise<WorkflowListResult> {
  try {
    const response = await client.fetch("/workflows");
    if (response.status === 404) return { kind: "unsupported" };
    if (!response.ok) return { kind: "unavailable" };
    const workflows = parseWorkflowList(await response.json());
    return workflows ? { kind: "ok", workflows } : { kind: "unavailable" };
  } catch {
    return { kind: "unavailable" };
  }
}

/** A card's text in the reader's language; the renderer's `zh` is the agent's `zh-TW`. English when a translation is missing. */
export function workflowText(item: Pick<WorkflowListing, "name" | "description">, locale: Locale): { title: string; description: string } {
  const key = locale === "zh" ? "zh-TW" : "en";
  return { title: item.name[key] || item.name.en, description: item.description[key] || item.description.en };
}

/** What a workflow is based on (the manifest's optional `about`), in the reader's language; null when it says nothing. */
export function workflowAbout(item: Pick<WorkflowListing, "about">, locale: Locale): string | null {
  if (!item.about) return null;
  const key = locale === "zh" ? "zh-TW" : "en";
  return item.about[key] || item.about.en || null;
}
